import type React from "react";
import { useContext, useEffect, useMemo, useState } from "react";

import { faNoteMedical } from "@fortawesome/pro-regular-svg-icons";
import { GridActionsCellItem, type GridColDef, type GridSortModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import invariant from "ts-invariant";
import { v4 as uuidv4 } from "uuid";

import { useGetCareEvent } from "api/hooks/useGetCareEvent";
import { useGetCareEventsSuggested } from "api/hooks/useGetCareEventsSuggested";
import { useGetPatients } from "api/hooks/useGetPatients";
import { useGetPTCareEventsQuery } from "api/hooks/useGetPTCareEventsQuery";
import { usePostCareEventSuggested } from "api/hooks/usePostCareEventSuggested";
import type { CareEvent } from "api/models/CareEvent";
import { SmallBlueClock } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { StatusUpdateDialog } from "routes/admin-billing/components";
import { DynamicMedicalNoteForm } from "routes/patients/PatientProfile/components/PatientHeader/SignNotesTable/components/DynamicMedicalNoteForm";
import { MedicalNoteLabel } from "routes/patients/PatientProfile/components/PatientHeader/SignNotesTable/components/MedicalNoteLabel";
import { removeNote } from "routes/patients/queries/notesApiHandler";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";
import { PremiumIcon } from "shared/atoms/PremiumIcon";
import { Tooltip } from "shared/atoms/Tooltip";
import { CircleCheckIcon, CircleXmarkIcon, UserMagnifyingGlassIcon } from "shared/icons";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import type { MedicalNoteType } from "types";
import { MarketTypes, MedicalNoteTypes } from "types";
import { CurrentPatientContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";
import { useWindowSize } from "utils/hooks";
import { isUserMarket } from "utils/profile/profileHelper";
import { getStorageValue, removeStorageValue, setStorageValue } from "utils/storage";

import { CommunicationGroupIcon } from "./CommunicationGroupIcon";
import { AfterNameIcon, Closed, LabelContainer, Name, NameContainer } from "./styles";

const paginationModel = { page: 0, pageSize: 10 };
const initialSortModel: GridSortModel = [{ field: "start_time", sort: "desc" }];

export const MedicalNotesTable: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { parseISO, format, isValid } = useLocalizedDate();
  const { profile } = useProfileContext();
  const { patient, setPatientId } = useContext(CurrentPatientContext);
  const createCareEventSuggested = usePostCareEventSuggested();
  invariant(profile, "Profile should be defined");
  const isSE = isUserMarket(MarketTypes.SE, profile);
  const { isLargeScreen } = useWindowSize();
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel);

  const {
    data: careEvents = [],
    isLoading: careEventsIsLoading,
    refetch: refetchPTCareEvents,
  } = useGetPTCareEventsQuery({ userId: profile.id });

  const { data: patients } = useGetPatients({ userID: profile.id });
  const patientIDs = patients?.map(p => p.id) ?? [];
  const {
    data: suggestedCareEvents = [],
    isLoading: suggestedCareEventsIsLoading,
    refetch: refetchSuggested,
  } = useGetCareEventsSuggested(patientIDs, {
    enabled: patientIDs.length > 0,
  });
  const isLoading = careEventsIsLoading || suggestedCareEventsIsLoading;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedCareEvent, setSelectedCareEvent] = useState<any | null>(null);
  const [showCareEvent, setShowCareEvent] = useState(false);
  const { data: careEvent } = useGetCareEvent(selectedCareEvent ? selectedCareEvent?.id : 0, {
    enabled: Boolean(selectedCareEvent),
  });
  const [showMarkAsDiscarded, setShowMarkAsDiscarded] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);

  useEffect(() => {
    const storedMedicalNoteSorting = getStorageValue("medical-notes-sort");
    if (storedMedicalNoteSorting) {
      if (Array.isArray(storedMedicalNoteSorting)) {
        setSortModel(storedMedicalNoteSorting);
      } else {
        // Clean up invalid storge value
        removeStorageValue("medical-notes-sort");
      }
    }
  }, []);

  const rows = useMemo(() => {
    if (isLoading) {
      return [];
    }

    if (!careEvents || !suggestedCareEvents) {
      return [];
    }

    const mappedSuggestedEvents = suggestedCareEvents.map(event => ({
      ...event,
      id: uuidv4(),
      suggested: true,
    }));

    return [...careEvents, ...mappedSuggestedEvents];
  }, [careEvents, suggestedCareEvents]);

  useEffect(() => {
    if (careEvent) {
      setPatientId(careEvent.patient_id);
    } else {
      setPatientId(null);
    }
  }, [careEvent, setPatientId]);

  const createCareEvent = (note: CareEvent, showDiscardCE: boolean) => {
    const { cpt_codes, label, message_interaction_id, rtm_care_event_log_id, user_course_week_id } = note;

    if (label && (message_interaction_id || rtm_care_event_log_id || user_course_week_id)) {
      createCareEventSuggested.mutateAsync(
        {
          data: {
            cpt_codes,
            label,
            message_interaction_id,
            rtm_care_event_log_id,
            user_course_week_id,
          },
          patientID: note.patient_id,
        },
        {
          onSuccess(careEventCreateData) {
            refetchPTCareEvents();
            refetchSuggested();
            setSelectedCareEvent({ id: careEventCreateData.id });
            if (showDiscardCE) {
              setShowMarkAsDiscarded(true);
            } else {
              setShowCareEvent(true);
            }
          },
        }
      );
    }
  };

  const discardFromDashboard = async () => {
    refetchPTCareEvents();
    refetchSuggested();
    setSelectedCareEvent(null);
  };

  const isActionable = (label: MedicalNoteType) => {
    if (isSE) {
      return label.toLocaleUpperCase() === MedicalNoteTypes.CHAT_HYBRID;
    }
    return true;
  };

  const isSpecialLabelState = (state: string) =>
    ["in_selfcare", "treatment_ended", "explore", "discharged"].includes(state);

  const columns: GridColDef[] = [
    {
      field: "patient_name",
      headerName: t("dashboard.tables.notes.labels.patient"),
      renderCell: ({ row }) => {
        return (
          <NameContainer>
            <PremiumIcon size="small" premiumType={row.premium_type} margin="0 4px 0 0" />
            <Name $label_state={isSpecialLabelState(row.patient_state)}>
              {row.patient_name}
              {row.patient_deleted && <Closed data-testid="account-closed">{t("errors.account_closed")}</Closed>}
              {!row.patient_deleted && row.patient_state === "treatment_ended" && (
                <Closed data-testid="treatment-ended">{t("errors.account_treatment_ended")}</Closed>
              )}
              {!row.patient_deleted && row.patient_state === "discharged" && (
                <Closed data-testid="discharged">{t("errors.account_discharged")}</Closed>
              )}
              {!row.patient_deleted && row.patient_state === "in_selfcare" && (
                <Closed data-testid="in-selfcare">{t("errors.account_in_selfcare")}</Closed>
              )}
              {!row.patient_deleted && row.patient_state === "explore" && (
                <Closed data-testid="explore">{t("errors.account_explore")}</Closed>
              )}
            </Name>
            {row.therapist_assignment_role === "substitute_therapist" && (
              <AfterNameIcon title={t("patients.tool_tip.substitute_therapist")}>
                <SmallBlueClock />
              </AfterNameIcon>
            )}
          </NameContainer>
        );
      },
      flex: 1,
    },
    {
      field: "label",
      headerName: t("dashboard.tables.notes.labels.type"),
      renderCell: ({ row }) => {
        return (
          <LabelContainer>
            {row.communication_group && <CommunicationGroupIcon communicationGroup={row.communication_group} />}
            <MedicalNoteLabel weekNumber={row.week_number} careEventLabel={row.label} />
          </LabelContainer>
        );
      },
      flex: 1,
    },
    {
      field: "start_time",
      headerName: t("dashboard.tables.notes.labels.date"),
      valueFormatter: (value: string) => {
        const date = value ? parseISO(value) : new Date();
        return isValid(date) ? format(date, "P") : "";
      },
      flex: 1,
      maxWidth: 120,
    },
    {
      field: "actions",
      type: "actions",
      width: 120,
      getActions: params =>
        isActionable(params.row.label)
          ? [
              <Tooltip title={t("dashboard.tables.notes.labels.sign_note")}>
                <GridActionsCellItem
                  icon={<CircleCheckIcon />}
                  label={t("dashboard.tables.notes.labels.sign_note")}
                  onClick={() => {
                    if (params.row.suggested) {
                      createCareEvent(params.row, false);
                    } else {
                      setSelectedCareEvent({ id: params.row.id });
                      setShowCareEvent(true);
                    }
                  }}
                />
              </Tooltip>,
              <Tooltip title={t("dashboard.tables.notes.labels.discard")}>
                <GridActionsCellItem
                  icon={<CircleXmarkIcon />}
                  label={t("dashboard.tables.notes.labels.discard")}
                  onClick={() => {
                    if (params.row.suggested) {
                      createCareEvent(params.row, true);
                    } else {
                      setSelectedCareEvent({ id: params.row.id });
                      setShowMarkAsDiscarded(true);
                    }
                  }}
                />
              </Tooltip>,
              <Tooltip title={t("booking.buttons.view_profile")}>
                <GridActionsCellItem
                  icon={<UserMagnifyingGlassIcon />}
                  label={t("booking.buttons.view_profile")}
                  showInMenu={false}
                  onClick={() => {
                    navigate(`/patients/${params.row.patient_id}`);
                  }}
                />
              </Tooltip>,
            ]
          : [],
    },
  ];

  return (
    <>
      <MUIDataGrid
        columns={columns}
        rows={rows}
        getRowId={row => row.id}
        loading={isLoading}
        initialState={{
          columns: {
            columnVisibilityModel: {
              patient_name: true,
              label: true,
              start_time: Boolean(isLargeScreen),
            },
          },
          pagination: { paginationModel },
        }}
        onCellClick={({ field, row }) => {
          if (field !== "actions") {
            navigate(`/patients/${row.patient_id}`, {
              state: {
                pathname,
                text: "dashboard",
                careEventID: row.id,
              },
            });
          }
        }}
        sortModel={sortModel}
        onSortModelChange={model => {
          setSortModel(model);
          setStorageValue("medical-notes-sort", model);
        }}
        rowSelection={false}
        slots={{
          noRowsOverlay: () => <EmptyResultOverlay text={t("dashboard.tables.notes.no_notes")} icon={faNoteMedical} />,
          noResultsOverlay: () => <EmptyResultOverlay />,
        }}
        localeText={{
          noRowsLabel: "",
          noResultsOverlayLabel: "",
        }}
        title={t("dashboard.tables.notes.title")}
        notifications={rows?.length}
      />

      {careEvent && showCareEvent && patient && (
        <MUIUnifiedModal
          open
          onClose={() => {
            setSelectedCareEvent(null);
            setShowCareEvent(false);
          }}
          width="770px"
        >
          <DynamicMedicalNoteForm
            careEvent={careEvent}
            open
            discardFromDashboard={discardFromDashboard}
            skipMerge
            showNoteHeader
          />
        </MUIUnifiedModal>
      )}

      <StatusUpdateDialog
        open={showMarkAsDiscarded && Boolean(selectedCareEvent)}
        status="discarded"
        count={1}
        isLoading={isLoading}
        error={customError}
        onConfirm={async () => {
          try {
            await removeNote(selectedCareEvent.id);
            refetchPTCareEvents();
            refetchSuggested();
            setShowMarkAsDiscarded(false);
          } catch {
            setCustomError(t("errors.generic"));
          }
        }}
        onCancel={() => setShowMarkAsDiscarded(false)}
      />
    </>
  );
};
